import { ApplicationDefinitionAdapter } from "./application-definition-adapter";
import { RegisteredApplication } from "si-mfes-library/framework/registered-application";
import { MfeFrameworkLoader } from "../framework/mfe-framework-loader";
import { IAuthenticationService } from "sirius-platform-support-library/shared/authentication/authentication-service.interface";
import { CurrentUserData } from "si-mfes-library/users/current-user-data.model";
import { UserStatus } from "si-mfes-library/users/user-status-enum";
import { ILocalizationService } from "sirius-platform-support-library/shared/localization/localization-service.interface";
import { sanitizeUrl } from "@braintree/sanitize-url";
import { environment } from "../../../environments/environments";
import DOMPurify from "dompurify";
import { ObjectUtility } from "sirius-platform-support-library/utilities/object-utility";

export class RoutesBootstrap {
  public static readonly TDP_NAVBAR_MFE_NAME = "tdp-navbar-mfe";

  private readonly window: Window;
  private readonly mfeFrameworkLoader: MfeFrameworkLoader;
  private readonly applicationDefinitionAdapter: ApplicationDefinitionAdapter;
  private readonly authenticationService: IAuthenticationService;
  private readonly localizationService: ILocalizationService;

  private readonly bindedOnBeforeAppChange = this.onBeforeAppChange.bind(this);
  private headerBootstrapFunction?: () => Promise<void>;
  private navbarObserver?: MutationObserver;
  private navbarElement?: Element;

  private readonly defaultNavigationHandler = (event: CustomEvent) => {
    const newEvent = this.replaceLangUrl(
      event,
      this.localizationService.getCurrentLocaleCode()
    );
    this.onNavigate(newEvent);
  };

  constructor(
    window: Window,
    mfeFrameworkLoader: MfeFrameworkLoader,
    applicationDefinitionAdapter: ApplicationDefinitionAdapter,
    authenticationService: IAuthenticationService,
    localizationService: ILocalizationService
  ) {
    this.window = window;
    this.mfeFrameworkLoader = mfeFrameworkLoader;
    this.applicationDefinitionAdapter = applicationDefinitionAdapter;
    this.authenticationService = authenticationService;
    this.localizationService = localizationService;
  }

  public async bootstrap(
    headerBootstrapFunction: () => Promise<void>
  ): Promise<void> {
    this.headerBootstrapFunction = headerBootstrapFunction;

    const applicationDefinitions =
      await this.applicationDefinitionAdapter.getApplicationDefinitions();
    const registeredApplications =
      applicationDefinitions.map<RegisteredApplication>((d) => ({
        name: d.name,
        route: d.route,
      }));
    this.mfeFrameworkLoader
      .getFramework()
      .setRegisteredApplications(registeredApplications);

    this.window.addEventListener(
      "single-spa:before-app-change",
      this.bindedOnBeforeAppChange
    );
    await this.bootstrapUserData();
  }

  public async bootstrapUserData(): Promise<void> {
    const userContext = this.authenticationService.getUserContext();
    if (userContext) {
      const currentUserData: CurrentUserData = {
        userStatus:
          (userContext?.claims["userStatus"] as unknown as UserStatus) ||
          UserStatus.UNKNOWN,
        displayName: userContext?.displayName,
        email: userContext?.claims["email"],
        authenticatedAsImpersonator:
          userContext?.claims["authenticatedAsImpersonator"]?.toString() ===
          "true",
        touAction: userContext?.claims["touAction"],
      };
      if (
        currentUserData.userStatus.toString() === UserStatus.ACTIVE.toString()
      ) {
        this.mfeFrameworkLoader
          .getFramework()
          .CurrentUser.setCurrentUser(currentUserData);
      }
    }
  }

  private async onBeforeAppChange(event: any): Promise<void> {
    this.mfeFrameworkLoader
      .getFramework()
      .LinkNavigator.registerNavigationHandler(
        "default",
        this.defaultNavigationHandler
      );

    const navbarAppKey = Object.keys(event?.detail?.newAppStatuses ?? {}).find(
      (ak) => ak?.toLowerCase()?.includes(RoutesBootstrap.TDP_NAVBAR_MFE_NAME)
    );
    const navbarLoaded =
      (event?.detail?.newAppStatuses ?? {})[navbarAppKey] === "MOUNTED";
    if (navbarLoaded) {
      this.setupNavbarObserver(navbarAppKey);
    }
  }

  public setupNavbarObserver(navbarHostElementName: string): void {
    if (
      this.navbarElement ||
      this.navbarObserver ||
      !this.headerBootstrapFunction
    ) {
      return;
    }

    this.navbarObserver = new MutationObserver(
      async (mutationsList, observer) => {
        for (const mutation of mutationsList) {
          if (mutation.addedNodes.length) {
            const hasNavbarMfe = Array.from(mutation.addedNodes).some(
              (node) =>
                node.nodeName.toLowerCase() ===
                RoutesBootstrap.TDP_NAVBAR_MFE_NAME.toLowerCase()
            );
            if (hasNavbarMfe) {
              try {
                await this.headerBootstrapFunction();
              } catch (e) {
                console.error(e);
              } finally {
                this.navbarObserver.disconnect();
                this.navbarObserver = undefined;
              }
            }
          }
        }
      }
    );

    const element = document.getElementById(
      `single-spa-application:${navbarHostElementName}`
    );
    if (!element) {
      return;
    }
    this.navbarObserver.observe(element, { childList: true });
  }

  public replaceLangUrl(event: CustomEvent, newLang: string): CustomEvent {
    if (!ObjectUtility.isDefined(event?.detail?.href)) {
      return;
    }
    const href = DOMPurify.sanitize(event.detail.href);

    if (href.startsWith("/")) {
      if (!href.includes("lang")) {
        return event;
      } else {
        const langPattern = /^[a-z]{2}(_[A-Z]{2})?$/; // Example: "en" or "en-US"
        if (!langPattern.test(newLang)) {
          throw new Error("Invalid language parameter");
        }

        event.detail.href = href.replace(
          /lang=\w+/,
          `lang=${sanitizeUrl(newLang)}`
        );
        return event;
      }
    } else {
      const parsedUrl = new URL(href);
      const params = new URLSearchParams(parsedUrl.search);
      params.set("lang", sanitizeUrl(newLang));
      event.detail.href = `${parsedUrl.origin}${
        parsedUrl.pathname
      }?${params.toString()}`;

      return event;
    }
  }

  public onNavigate(event: CustomEvent) {
    if (event?.detail?.href) {
      const sanitizedUrl = DOMPurify.sanitize(event.detail.href);
      if (event.detail?.target === "_blank") {
        this.openInNewTab(sanitizedUrl);
      } else if (
        sanitizedUrl.startsWith("/") ||
        sanitizedUrl.startsWith(environment.rootUrl)
      ) {
        if (
          !this.mfeFrameworkLoader
            .getFramework()
            .LinkNavigator.isSingleSpaRoute(sanitizedUrl)
        ) {
          this.openInSameTab(sanitizedUrl);
        } else {
          const relativeUrl = sanitizedUrl.replace(environment.rootUrl, "");
          this.openLocalUrl(relativeUrl);
        }
      } else {
        this.openInSameTab(sanitizedUrl);
      }
    }
  }

  public openInNewTab(url: string): void {
    this.window.open(url, "_blank");
  }

  public openLocalUrl(relativeUrl: string): void {
    this.window.history.pushState(null, null, relativeUrl);
  }

  public openInSameTab(url: string): void {
    this.window.location.href = url;
  }
}
